export type MarketingModule = {
  slug: string;
  name: string;
  eyebrow: string;
  promise: string;
  description: string;
  outcome: string;
  status?: "Beta";
  accent: string;
  capabilities: Array<{ title: string; description: string }>;
  outcomes: Array<{ title: string; description: string }>;
  useCases: string[];
  connectsWith: string[];
  visualPrompt: string;
};

export const marketingModules: MarketingModule[] = [
  {
    slug: "crm-conversaciones",
    name: "CRM y conversaciones",
    eyebrow: "Conoce y atiende a cada cliente",
    promise:
      "Cada conversación alimenta una relación que tu equipo puede continuar.",
    description:
      "Reúne WhatsApp, Instagram, Messenger, SMS, webchat, email y voz en el mismo historial del contacto. El pipeline, las actividades y los datos comerciales dejan de vivir en herramientas separadas.",
    outcome: "Menos contexto perdido. Más oportunidades que avanzan.",
    accent: "#3B82F6",
    capabilities: [
      {
        title: "Inbox omnicanal",
        description: "Todos los canales y asignaciones en una sola bandeja.",
      },
      {
        title: "Contactos y compañías",
        description:
          "Una ficha única para personas, cuentas B2B, etiquetas, fuentes y campos personalizados.",
      },
      {
        title: "Deals y pipelines",
        description:
          "Oportunidades, etapas, productos, responsables y seguimiento conectados al contacto.",
      },
      {
        title: "Actividades",
        description:
          "Tareas, llamadas y calendario CRM en el mismo contexto comercial.",
      },
      {
        title: "Productos y catálogos",
        description:
          "Catálogo con categorías, media, búsqueda y enlaces públicos.",
      },
      {
        title: "Formularios",
        description:
          "Captura respuestas que crean o actualizan contactos reales.",
      },
    ],
    outcomes: [
      {
        title: "Responde con contexto",
        description:
          "El equipo ve el historial antes de contestar, sin pedir al cliente que repita su historia.",
      },
      {
        title: "Convierte mientras conversa",
        description:
          "Crea oportunidades, actividades y cotizaciones desde el mismo flujo de atención.",
      },
      {
        title: "Mantiene la propiedad clara",
        description:
          "Asignaciones, responsables y handoffs hacen visible quién debe actuar.",
      },
    ],
    useCases: [
      "Ventas por WhatsApp",
      "Atención omnicanal",
      "Seguimiento B2B",
      "Catálogos conversacionales",
    ],
    connectsWith: [
      "Asistentes de IA",
      "Automatizaciones",
      "Finanzas",
      "Mesa de ayuda",
    ],
    visualPrompt:
      "Video: una conversación de WhatsApp crea un contacto, abre un deal y programa una actividad.",
  },
  {
    slug: "asistentes-ia",
    name: "Asistentes de IA",
    eyebrow: "IA que trabaja dentro de tu negocio",
    promise: "No solo responde: consulta, decide y ejecuta acciones reales.",
    description:
      "Configura asistentes para conversaciones, tickets, Nova y voz. Dales herramientas internas de Apex, APIs propias, servidores MCP, conocimiento especializado, reglas de transferencia y seguimiento.",
    outcome: "Atención inmediata sin aislar a la IA de tus procesos.",
    accent: "#C9B8FF",
    capabilities: [
      {
        title: "Herramientas internas",
        description:
          "Contactos, deals, reservas, productos, tickets, cotizaciones, facturas y más.",
      },
      {
        title: "Transferencia a personas",
        description:
          "Entrega por propietario, disponibilidad, pesos y reglas de respaldo.",
      },
      {
        title: "Conocimiento y memoria",
        description:
          "Conocimiento especializado con condiciones de uso e historial restaurable.",
      },
      {
        title: "APIs y MCP",
        description:
          "Conecta herramientas HTTP y servidores externos con OAuth o API key.",
      },
      {
        title: "Seguimiento automático",
        description:
          "Retoma conversaciones cuando el cliente deja de responder.",
      },
      {
        title: "Voz",
        description:
          "Agentes Telnyx con números, transferencias, variables y widget web.",
      },
    ],
    outcomes: [
      {
        title: "Resuelve más en el primer contacto",
        description:
          "La IA consulta el dato necesario y completa la acción sin derivaciones innecesarias.",
      },
      {
        title: "Escala sin perder control",
        description:
          "Tú eliges modelos, herramientas, horarios, permisos y cuándo interviene una persona.",
      },
      {
        title: "Conserva el aprendizaje",
        description:
          "El conocimiento, las instrucciones y el historial mantienen una operación consistente.",
      },
    ],
    useCases: [
      "Calificación comercial",
      "Reservas 24/7",
      "Soporte de primer nivel",
      "Agentes de voz",
    ],
    connectsWith: [
      "CRM y conversaciones",
      "Reservas",
      "Mesa de ayuda",
      "Automatizaciones",
    ],
    visualPrompt:
      "Video: un cliente pide mover una cita; la IA consulta disponibilidad, actualiza la reserva y confirma.",
  },
  {
    slug: "automatizaciones",
    name: "Automatizaciones",
    eyebrow: "El trabajo avanza aunque nadie lo persiga",
    promise: "Conecta lo que ocurre con la siguiente acción correcta.",
    description:
      "Crea flujos visuales activados por eventos de CRM, conversaciones, reservas, formularios, mesa de ayuda, voz, eventos, fechas o webhooks externos.",
    outcome: "Procesos repetibles, visibles y conectados a tus datos reales.",
    accent: "#9FE8FF",
    capabilities: [
      {
        title: "Triggers nativos",
        description:
          "Inicia por cambios reales en contactos, deals, tickets, reservas, eventos y llamadas.",
      },
      {
        title: "Acciones de negocio",
        description:
          "Crea o actualiza entidades y envía mensajes sin replicar datos afuera.",
      },
      {
        title: "Chat con IA",
        description:
          "Clasifica, resume o redacta información dentro del flujo.",
      },
      {
        title: "Control de asistentes",
        description:
          "Pausa, reanuda o reasigna la IA dentro de una conversación.",
      },
      {
        title: "Webhooks y HTTP",
        description: "Integra eventos externos en el mismo proceso operativo.",
      },
      {
        title: "Ejecuciones auditables",
        description: "Revisa entradas y salidas nodo por nodo.",
      },
    ],
    outcomes: [
      {
        title: "Reduce coordinación manual",
        description:
          "Las tareas y comunicaciones nacen del evento que realmente las requiere.",
      },
      {
        title: "Estandariza sin volver rígido el proceso",
        description: "Combina reglas, IA, ramas y acciones humanas.",
      },
      {
        title: "Encuentra dónde falló",
        description:
          "Cada ejecución conserva el recorrido y los datos de cada nodo.",
      },
    ],
    useCases: [
      "Lead routing",
      "Seguimientos",
      "SLA y escalamiento",
      "Recordatorios de reservas",
    ],
    connectsWith: [
      "CRM y conversaciones",
      "Asistentes IA",
      "Mesa de ayuda",
      "Eventos",
    ],
    visualPrompt:
      "Video: formulario recibido → contacto → asignación → deal → WhatsApp → tarea.",
  },
  {
    slug: "finance",
    name: "Finanzas",
    status: "Beta",
    eyebrow: "De la oportunidad al dinero",
    promise:
      "Cotiza, factura, cobra y controla pagos sin desprenderte del cliente.",
    description:
      "Gestiona cuentas por cobrar y pagar: cotizaciones, facturas, pagos, enlaces de cobro, recurrencia, proveedores, gastos y obligaciones, con documentos y monedas configurables. No sustituye la contabilidad de partida doble ni la facturación electrónica fiscal.",
    outcome:
      "La operación comercial y el movimiento de dinero comparten contexto.",
    accent: "#B8F4D2",
    capabilities: [
      {
        title: "Cotizaciones",
        description:
          "Documentos ligados al contacto y deal, con vista pública y seguimiento.",
      },
      {
        title: "Facturas y pagos",
        description:
          "Conversión desde cotización, pagos parciales y asignación multi-factura.",
      },
      {
        title: "Enlaces de pago",
        description: "Pago público con Stripe o Wompi.",
      },
      {
        title: "Recurrencia",
        description:
          "Perfiles para facturas, gastos y cuentas por pagar periódicas.",
      },
      {
        title: "Cuentas por pagar",
        description:
          "Proveedores, gastos, obligaciones, pagos realizados y asignaciones.",
      },
      {
        title: "Documentos",
        description: "Plantillas PDF, numeración, impuestos, monedas y FX.",
      },
    ],
    outcomes: [
      {
        title: "Cobra con menos fricción",
        description:
          "Comparte documentos y enlaces de pago desde la relación comercial.",
      },
      {
        title: "Ve lo pendiente",
        description:
          "Distingue obligaciones, pagos parciales y saldos por cliente o proveedor.",
      },
      {
        title: "Reutiliza tu estándar",
        description:
          "Plantillas, mensajes, numeración y recurrencia reducen trabajo repetitivo.",
      },
    ],
    useCases: [
      "Servicios profesionales",
      "Ventas consultivas",
      "Cobros recurrentes",
      "Control de proveedores",
    ],
    connectsWith: [
      "Oportunidades",
      "Conversaciones",
      "Automatizaciones",
      "Contactos",
    ],
    visualPrompt:
      "Video: deal aprobado → cotización → aceptación pública → factura → pago.",
  },
  {
    slug: "operations",
    name: "Operaciones",
    eyebrow: "Convierte promesas en trabajo entregado",
    promise:
      "Proyectos, tareas y objetivos conectados con lo que el cliente compró.",
    description:
      "Organiza proyectos, scope, roadmap, documentos, deliverables, equipos, tareas y estrategia/OKRs. El trabajo puede nacer directamente de la venta o una automatización.",
    outcome: "Tu equipo sabe qué hacer, por qué y para quién.",
    accent: "#FFD59E",
    capabilities: [
      {
        title: "Proyectos",
        description:
          "Workspaces con alcance, roadmap, documentos, entregables y miembros.",
      },
      {
        title: "Tareas",
        description: "Trabajo por proyecto y vista personal cross-project.",
      },
      {
        title: "Equipos",
        description: "Agrupa responsables operativos y asignaciones.",
      },
      {
        title: "Estrategia y OKRs",
        description: "Ciclos, objetivos, key results y tareas relacionadas.",
      },
      {
        title: "Reportes",
        description: "Lectura de ejecución para equipos y responsables.",
      },
      {
        title: "Conexión comercial",
        description:
          "El contacto y el deal acompañan el trabajo que originaron.",
      },
    ],
    outcomes: [
      {
        title: "Entrega lo vendido",
        description:
          "El alcance comercial puede transformarse en tareas y entregables concretos.",
      },
      {
        title: "Prioriza en un solo lugar",
        description:
          "Cada persona reúne sus tareas aunque participen en varios proyectos.",
      },
      {
        title: "Alinea ejecución y estrategia",
        description: "Los objetivos no quedan separados del trabajo diario.",
      },
    ],
    useCases: [
      "Agencias",
      "Consultoras",
      "Equipos de implementación",
      "Servicios recurrentes",
    ],
    connectsWith: ["CRM", "Automatizaciones", "Pizarras", "Nova"],
    visualPrompt:
      "Video: un deal ganado crea proyecto, entregables, responsables y fechas.",
  },
  {
    slug: "helpdesk",
    name: "Mesa de ayuda",
    status: "Beta",
    eyebrow: "Soporte con la historia completa",
    promise:
      "Resuelve tickets sabiendo qué se vendió, qué se prometió y qué ocurrió antes.",
    description:
      "Gestiona tickets con colas, asignación, SLA, macros, grupos, base de conocimiento, analítica y un portal público de autoservicio.",
    outcome: "Respuestas más rápidas sin hacer que el cliente empiece de cero.",
    accent: "#FFB8C6",
    capabilities: [
      {
        title: "Tickets",
        description:
          "Estados, prioridad, responsables, watchers, notas y conversación.",
      },
      {
        title: "SLA",
        description:
          "Objetivos de primera respuesta, siguiente respuesta y resolución.",
      },
      {
        title: "Knowledge base",
        description: "Artículos para el equipo y el portal de clientes.",
      },
      {
        title: "Portal público",
        description: "Autoservicio con marca y acceso desde /help.",
      },
      {
        title: "Routing y macros",
        description: "Distribuye y estandariza respuestas frecuentes.",
      },
      {
        title: "Analítica",
        description: "Analiza volumen y desempeño de soporte.",
      },
    ],
    outcomes: [
      {
        title: "Evita preguntas repetidas",
        description:
          "El ticket conserva la identidad y el historial del contacto.",
      },
      {
        title: "Protege tus compromisos",
        description:
          "Los SLA y automatizaciones detectan y escalan incumplimientos.",
      },
      {
        title: "Reduce tickets evitables",
        description:
          "La base de conocimiento y el portal ofrecen autoservicio.",
      },
    ],
    useCases: [
      "Customer success",
      "Soporte B2B",
      "Mesa de ayuda",
      "Atención postventa",
    ],
    connectsWith: [
      "CRM y conversaciones",
      "Asistentes IA",
      "Automatizaciones",
      "Nova Support",
    ],
    visualPrompt:
      "Video: ticket recibido con historial del contacto, deal, conversación y SLA visibles.",
  },
  {
    slug: "reservations",
    name: "Reservas",
    eyebrow: "Agenda sin idas y vueltas",
    promise:
      "Disponibilidad, recursos, reservas y recordatorios en el mismo sistema.",
    description:
      "Define servicios, recursos y horarios; publica enlaces de reserva; administra citas en calendario y automatiza confirmaciones y recordatorios.",
    outcome: "Más reservas completadas y menos coordinación manual.",
    accent: "#C9E6FF",
    capabilities: [
      {
        title: "Servicios reservables",
        description: "Duración, reglas y configuración por tipo de cita.",
      },
      {
        title: "Recursos",
        description: "Personas, espacios o equipos con disponibilidad propia.",
      },
      {
        title: "Enlaces de reserva",
        description: "Enlaces de reserva para el equipo y sus servicios.",
      },
      {
        title: "Calendario",
        description: "Vista operativa para crear, mover y revisar reservas.",
      },
      {
        title: "Reserva pública",
        description: "Autoservicio conectado al contacto de Apex.",
      },
      {
        title: "Notificaciones",
        description:
          "Confirmaciones y recordatorios por email y automatización.",
      },
    ],
    outcomes: [
      {
        title: "Elimina coordinación",
        description:
          "El cliente elige únicamente entre slots realmente disponibles.",
      },
      {
        title: "Protege capacidad",
        description: "Servicios y recursos evitan asignaciones incompatibles.",
      },
      {
        title: "Activa el siguiente paso",
        description:
          "Una reserva puede iniciar mensajes, actividades o procesos completos.",
      },
    ],
    useCases: ["Clínicas", "Salones", "Consultorías", "Servicios con recursos"],
    connectsWith: [
      "Contactos",
      "Asistentes IA",
      "Automatizaciones",
      "Reuniones",
    ],
    visualPrompt:
      "Video: reserva pública → horario disponible → reserva → confirmación → recordatorio.",
  },
  {
    slug: "events",
    name: "Eventos",
    eyebrow: "Del registro al seguimiento",
    promise:
      "Crea eventos, vende entradas, registra asistencia y conserva cada relación.",
    description:
      "Gestiona eventos, invitados, tickets, cupones, registro público, check-in QR, directorio de personas y analítica.",
    outcome:
      "Cada asistente se convierte en un contacto accionable, no en una lista aislada.",
    accent: "#F7C6FF",
    capabilities: [
      {
        title: "Gestión de eventos",
        description: "Configuración, fechas, contenido y operación del evento.",
      },
      {
        title: "Registro público",
        description: "Páginas para inscripción, tickets y cupones.",
      },
      {
        title: "Invitados",
        description: "Roster de registros, estado y datos asociados.",
      },
      {
        title: "Check-in QR",
        description: "Control de asistencia desde el evento.",
      },
      { title: "People", description: "Directorio consolidado de asistentes." },
      {
        title: "Analítica",
        description: "Métricas de registro y participación.",
      },
    ],
    outcomes: [
      {
        title: "Opera sin hojas dispersas",
        description: "Registro y asistencia viven en la misma estructura.",
      },
      {
        title: "Continúa la relación",
        description:
          "Cada persona queda conectada al CRM para campañas y seguimiento.",
      },
      {
        title: "Reacciona a la asistencia",
        description: "Registro y check-in pueden disparar automatizaciones.",
      },
    ],
    useCases: [
      "Conferencias",
      "Talleres",
      "Lanzamientos",
      "Eventos de comunidad",
    ],
    connectsWith: [
      "CRM",
      "Automatizaciones",
      "Finanzas",
      "Enlaces inteligentes",
    ],
    visualPrompt:
      "Video: registro público → ticket QR → check-in → automatización postevento.",
  },
  {
    slug: "meetings",
    name: "Reuniones",
    eyebrow: "Las reuniones dejan memoria útil",
    promise:
      "Agenda, graba, transcribe y comparte sin perder la relación con el cliente.",
    description:
      "Usa enlaces personales de reserva, bots de grabación y transcripción, captura por micrófono, resúmenes y transcripciones públicas.",
    outcome:
      "Decisiones y contexto disponibles después de que termina la llamada.",
    accent: "#FFE29A",
    capabilities: [
      {
        title: "Reservas personales",
        description: "Enlaces de disponibilidad para reuniones uno a uno.",
      },
      {
        title: "Grabación",
        description: "Bot Recall.ai o captura desde micrófono.",
      },
      {
        title: "Transcripción",
        description: "Texto consultable y asociado a la reunión.",
      },
      {
        title: "Resumen",
        description: "Síntesis para retomar acuerdos y próximos pasos.",
      },
      {
        title: "Compartir",
        description: "Transcripción pública mediante token.",
      },
      {
        title: "Configuración",
        description: "Defaults de organización y opciones de publicación.",
      },
    ],
    outcomes: [
      {
        title: "Recuerda los acuerdos",
        description:
          "La conversación queda disponible para seguimiento y ejecución.",
      },
      {
        title: "Comparte sin reenviar archivos",
        description: "Un enlace público controlado presenta la transcripción.",
      },
      {
        title: "Reduce notas manuales",
        description:
          "Grabación, transcripción y resumen mantienen el foco en la reunión.",
      },
    ],
    useCases: [
      "Discovery comercial",
      "Consultoría",
      "Customer success",
      "Reuniones internas",
    ],
    connectsWith: ["Contactos", "Actividades", "Operaciones", "Nova"],
    visualPrompt:
      "Video: reserva → bot en la reunión → transcripción → resumen → tarea de seguimiento.",
  },
  {
    slug: "sites",
    name: "Sitios",
    eyebrow: "Publica sin separar captación y operación",
    promise:
      "Crea sitios completos con IA y convierte sus visitas en datos de Apex.",
    description:
      "Construye sitios multipágina con navegación, temas, recursos, SEO, versiones y publicación en subdominio gratuito o dominio propio.",
    outcome: "Tu presencia web vive cerca del CRM, los formularios y la IA.",
    accent: "#BEE8FF",
    capabilities: [
      {
        title: "Sitios multipágina",
        description:
          "Home y páginas adicionales con header, footer y navegación compartida.",
      },
      {
        title: "IA para sitios",
        description:
          "Crea y modifica páginas, estilos, diseños e imágenes por chat.",
      },
      {
        title: "Contexto y conocimiento",
        description:
          "Mantén reglas de marca y conocimiento entre conversaciones.",
      },
      {
        title: "Formularios Apex",
        description:
          "Inserta formularios que alimentan datos reales de la organización.",
      },
      {
        title: "SEO y preview",
        description: "Configura metadatos y revisa desktop, tablet y mobile.",
      },
      {
        title: "Publicación",
        description:
          "Subdominio gratuito, dominio propio y versiones restaurables.",
      },
    ],
    outcomes: [
      {
        title: "Lanza más rápido",
        description:
          "IA, importación HTML y tema compartido aceleran el primer sitio.",
      },
      {
        title: "Mantiene consistencia",
        description:
          "Diseños, memoria, conocimiento y versiones protegen las reglas de marca.",
      },
      {
        title: "Captura dentro del sistema",
        description: "Los formularios no terminan en una bandeja desconectada.",
      },
    ],
    useCases: [
      "Sitios de servicios",
      "Micrositios",
      "Campañas",
      "Páginas corporativas",
    ],
    connectsWith: ["Formularios", "CRM", "IA", "S3 y CloudFront"],
    visualPrompt:
      "Video: prompt → sitio multipágina → preview responsive → publicación en dominio.",
  },
  {
    slug: "presentations",
    name: "Presentaciones",
    eyebrow: "De una idea a un deck compartible",
    promise:
      "Crea presentaciones con IA, trabaja en borrador y publica solo la versión aprobada.",
    description:
      "Construye diapositivas HTML/CSS, aplica tema y marca, genera recursos, conserva notas, maneja versiones y comparte una versión Live o PDF.",
    outcome: "Entregables visuales más rápidos, consistentes y controlados.",
    accent: "#D7CCFF",
    capabilities: [
      {
        title: "IA para presentaciones",
        description:
          "Crea, reordena y edita slides; genera imágenes y consulta fuentes públicas.",
      },
      {
        title: "Tema y marca",
        description: "Mantén una identidad visual reutilizable.",
      },
      {
        title: "Notas del orador",
        description: "Guion sincronizado para presentar.",
      },
      {
        title: "Borrador y Live",
        description: "Los cambios internos no alteran lo que ya compartiste.",
      },
      {
        title: "Enlace público",
        description: "Presenta mediante token sin exponer el builder.",
      },
      {
        title: "PDF",
        description: "Descarga la versión aprobada para distribución.",
      },
    ],
    outcomes: [
      {
        title: "Produce en menos ciclos",
        description:
          "La IA inicia el contenido y el equipo refina lo importante.",
      },
      {
        title: "Comparte con confianza",
        description: "La versión Live queda separada del borrador en edición.",
      },
      {
        title: "Reutiliza lo que funciona",
        description:
          "Duplica presentaciones y conserva temas, contexto y conocimiento.",
      },
    ],
    useCases: ["Propuestas", "Capacitaciones", "Reportes", "Presentaciones"],
    connectsWith: ["CRM", "Sitios", "S3", "Nova"],
    visualPrompt:
      "Video: brief → deck generado → edición → Publish Live → presentación pública.",
  },
  {
    slug: "whiteboards",
    name: "Pizarras",
    eyebrow: "Piensa visualmente con datos vivos",
    promise:
      "Ideas, diagramas y entidades de Apex conviven en el mismo canvas.",
    description:
      "Crea mapas con texto, media, enlaces y nodos vivos de contactos, deals, productos, tareas y proyectos. Usa IA para generar, conectar y organizar.",
    outcome: "La estrategia visual permanece conectada a la ejecución.",
    accent: "#FFC9A8",
    capabilities: [
      {
        title: "Canvas flexible",
        description: "Notas, formas, media, enlaces y conexiones.",
      },
      {
        title: "Nodos CRM vivos",
        description: "Contactos, deals y productos dentro del mapa.",
      },
      {
        title: "Operaciones en contexto",
        description: "Tareas, proyectos y roadmap visibles en el canvas.",
      },
      {
        title: "IA para pizarras",
        description: "Genera y organiza nodos e imágenes sobre el documento.",
      },
      {
        title: "Versiones",
        description: "Deshaz y restaura cambios importantes.",
      },
      {
        title: "Vista pública",
        description: "Comparte una versión de solo lectura por enlace.",
      },
    ],
    outcomes: [
      {
        title: "Conecta idea y realidad",
        description:
          "El mapa puede referenciar los registros que el equipo ya opera.",
      },
      {
        title: "Alinea equipos",
        description: "Un mismo canvas reúne contexto, decisiones y trabajo.",
      },
      {
        title: "Empieza sin lienzo en blanco",
        description: "La IA puede proponer estructura y contenido inicial.",
      },
    ],
    useCases: [
      "Mapas de cuenta",
      "Plan de campaña",
      "Arquitectura de proyectos",
      "Workshops",
    ],
    connectsWith: ["CRM", "Operaciones", "Presentaciones", "IA"],
    visualPrompt:
      "Video: brief → mapa IA → contacto y deal vivos → tareas conectadas → enlace público.",
  },
  {
    slug: "contracts",
    name: "Contratos",
    status: "Beta",
    eyebrow: "Acuerdos listos para avanzar",
    promise:
      "Genera, envía y firma contratos con datos que ya existen en tu CRM.",
    description:
      "Administra plantillas reutilizables, variables de Apex, ciclo de vida contractual, firma pública y evidencia de auditoría.",
    outcome: "Menos copiar y pegar entre la venta y el acuerdo firmado.",
    accent: "#D9F0B1",
    capabilities: [
      {
        title: "Plantillas",
        description: "Estructuras reutilizables para acuerdos frecuentes.",
      },
      {
        title: "Variables CRM",
        description: "Datos del contacto y negocio dentro del documento.",
      },
      {
        title: "Ciclo de vida",
        description: "Crea, prepara, envía y sigue el estado del contrato.",
      },
      {
        title: "Firma pública",
        description: "Experiencia externa mediante token.",
      },
      {
        title: "Auditoría",
        description: "Conserva eventos relevantes del proceso.",
      },
      {
        title: "Relación comercial",
        description: "El acuerdo permanece conectado al cliente.",
      },
    ],
    outcomes: [
      {
        title: "Prepara con menos errores",
        description: "Las variables reducen la reescritura de datos conocidos.",
      },
      {
        title: "Acorta el cierre",
        description:
          "El flujo público permite revisar y firmar sin una herramienta aislada.",
      },
      {
        title: "Conserva trazabilidad",
        description: "El estado y la auditoría acompañan al documento.",
      },
    ],
    useCases: [
      "Servicios profesionales",
      "Acuerdos comerciales",
      "Onboarding",
      "Contratos recurrentes",
    ],
    connectsWith: ["CRM", "Oportunidades", "Automatizaciones", "Finanzas"],
    visualPrompt:
      "Video: plantilla + datos CRM → contrato → firma pública → estado firmado.",
  },
  {
    slug: "smart-links",
    name: "Enlaces inteligentes",
    status: "Beta",
    eyebrow: "Cada enlace puede explicar qué ocurrió",
    promise: "Acorta, comparte y mide enlaces con atribución conectada al CRM.",
    description:
      "Crea enlaces cortos apx.red, códigos QR, parámetros UTM, actividad detallada y analítica agregada.",
    outcome: "Convierte clics en señales que ventas y marketing pueden usar.",
    accent: "#AEEBE1",
    capabilities: [
      {
        title: "Enlaces apx.red",
        description: "URLs cortas y administrables.",
      },
      {
        title: "QR",
        description: "Lleva campañas físicas al mismo seguimiento digital.",
      },
      { title: "UTM", description: "Conserva parámetros de adquisición." },
      {
        title: "Actividad",
        description: "Stream de clics y señales observadas.",
      },
      {
        title: "Analítica",
        description: "Métricas agregadas para comparar desempeño.",
      },
      {
        title: "Atribución CRM",
        description:
          "Relaciona interacción y contacto cuando el flujo lo permite.",
      },
    ],
    outcomes: [
      {
        title: "Mide más allá del clic",
        description:
          "La actividad puede formar parte del historial y las automatizaciones.",
      },
      {
        title: "Ordena campañas",
        description: "UTM, QR y enlaces de marca viven en una sola superficie.",
      },
      {
        title: "Activa seguimiento",
        description:
          "Las señales del enlace pueden alimentar acciones posteriores.",
      },
    ],
    useCases: [
      "Campañas",
      "QR en eventos",
      "Contenido comercial",
      "Atribución",
    ],
    connectsWith: ["CRM", "Eventos", "Automatizaciones", "Analítica"],
    visualPrompt:
      "Video: crear link + QR → clic → actividad → contacto atribuido → seguimiento.",
  },
  {
    slug: "plataforma-api",
    name: "Plataforma y API",
    eyebrow: "Extiende Apex sin crear otro silo",
    promise:
      "Conecta tus sistemas, agentes y proveedores sobre el mismo modelo de permisos y organización.",
    description:
      "Usa API REST, llaves por organización, el servidor MCP oficial y webhooks para operar datos de Apex desde tus propias aplicaciones y herramientas de IA.",
    outcome:
      "Tu tecnología existente se conecta con Apex sin perder control ni contexto.",
    accent: "#C9E6FF",
    capabilities: [
      {
        title: "API REST",
        description:
          "Consulta y opera recursos autorizados con llaves administrables.",
      },
      {
        title: "Servidor MCP oficial",
        description:
          "Conecta clientes de IA compatibles mediante autorización por organización.",
      },
      {
        title: "Webhooks y automatizaciones",
        description:
          "Recibe eventos externos y continúa procesos dentro de Apex.",
      },
      {
        title: "Permisos por organización",
        description:
          "Conserva el alcance de usuarios, equipos y recursos compartidos.",
      },
      {
        title: "Integraciones reales",
        description:
          "Meta, Telnyx, Stripe, Wompi, Recall.ai, Google, Microsoft y AWS.",
      },
      {
        title: "Wallet y consumo",
        description:
          "Centraliza el uso variable de IA, voz, transcripción y otros servicios.",
      },
    ],
    outcomes: [
      {
        title: "Integra sin duplicar operaciones",
        description:
          "Los sistemas externos trabajan sobre los mismos contactos y procesos.",
      },
      {
        title: "Lleva Apex a tus agentes",
        description:
          "El MCP permite usar capacidades autorizadas desde clientes compatibles.",
      },
      {
        title: "Mantén control",
        description:
          "Las credenciales y permisos definen qué puede hacer cada integración.",
      },
    ],
    useCases: [
      "Integraciones internas",
      "Agentes de IA externos",
      "Sincronización de datos",
      "Procesos con webhooks",
    ],
    connectsWith: ["CRM", "Asistentes de IA", "Automatizaciones", "Finanzas"],
    visualPrompt:
      "Imagen: API y MCP conectando herramientas externas con contactos, permisos y automatizaciones de Apex.",
  },
];

export const moduleGroups = [
  {
    label: "Vender y atender",
    slugs: [
      "crm-conversaciones",
      "asistentes-ia",
      "automatizaciones",
      "helpdesk",
    ],
  },
  {
    label: "Operar y cobrar",
    slugs: ["operations", "finance", "reservations", "events"],
  },
  {
    label: "Crear y compartir",
    slugs: ["sites", "presentations", "whiteboards", "contracts"],
  },
  {
    label: "Conectar y ampliar",
    slugs: ["meetings", "smart-links", "plataforma-api"],
  },
];

export const getMarketingModule = (slug: string) =>
  marketingModules.find((module) => module.slug === slug);
